'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useMemo, useRef, useState } from 'react';
import { cx } from '../../../internal/cx.js';
import { Avatar, AvatarGroup } from '../avatar/index.js';
import { EntityPicker } from '../../forms/entity-picker/index.js';
const DEFAULT_MESSAGES = {
    unassigned: 'Unassigned',
    you: (name) => `${name} (you)`,
    assignToMe: 'Assign to me',
    count: (n) => `${n} people`,
    search: 'Assign to…',
};
function UnassignedGlyph() {
    return (_jsx("svg", { width: 16, height: 16, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeDasharray: "2 2", "aria-hidden": true, focusable: false, children: _jsx("circle", { cx: "8", cy: "8", r: "6.25" }) }));
}
/**
 * People picker: assign one or several people from a searchable list with
 * avatars, the current user first, an "Unassigned" option and an "Assign to
 * me" shortcut. The trigger shows the assignee, or an avatar stack for
 * several. Built on EntityPicker and Avatar; the product supplies the people.
 */
export const AssigneePicker = forwardRef(function AssigneePicker({ people = [], onSearch, valuePeople, currentUserId = null, allowUnassigned = true, showAssignToMe = true, maxAvatars = 3, messages: messagesProp, multiple = false, value: valueProp, defaultValue = null, onValueChange, values: valuesProp, defaultValues, onValuesChange, placeholder, searchPlaceholder, footer, className, ...rest }, ref) {
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [internalValues, setInternalValues] = useState(defaultValues ?? []);
    const value = valueProp !== undefined ? valueProp : internalValue;
    const values = valuesProp ?? internalValues;
    // Everyone the picker has been given, for trigger names and avatars.
    const known = useRef(new Map());
    for (const p of valuePeople ?? [])
        known.current.set(p.id, p);
    for (const p of people)
        known.current.set(p.id, p);
    const toOption = (p) => ({
        value: p.id,
        label: p.id === currentUserId ? messages.you(p.name) : p.name,
        ...(p.email ? { description: p.email, keywords: `${p.name} ${p.email}` } : {}),
        ...(p.hint ? { hint: p.hint } : {}),
        ...(p.group ? { group: p.group } : {}),
        ...(p.disabled ? { disabled: true } : {}),
        media: (_jsx(Avatar, { name: p.name, src: p.avatarUrl ?? null, seed: p.id, size: "xs", decorative: true, ...(p.status ? { status: p.status } : {}) })),
    });
    // The current user first within their group; everyone else keeps the product's order.
    const meFirst = (list) => {
        const me = list.find((p) => p.id === currentUserId);
        if (!me)
            return list;
        const rest = list.filter((p) => p !== me);
        const at = rest.findIndex((p) => (p.group ?? '') === (me.group ?? ''));
        return at < 0 ? [...rest, me] : [...rest.slice(0, at), me, ...rest.slice(at)];
    };
    const options = useMemo(() => meFirst(people).map(toOption), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [people, currentUserId, messages.you]);
    const valueOptions = useMemo(() => (valuePeople ?? []).map(toOption), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valuePeople, currentUserId, messages.you]);
    const search = onSearch
        ? async (q, ctx) => {
            const found = await onSearch(q, ctx);
            for (const p of found)
                known.current.set(p.id, p);
            return meFirst(found).map(toOption);
        }
        : undefined;
    const setValue = (next) => {
        if (valueProp === undefined)
            setInternalValue(next);
        onValueChange?.(next);
    };
    const setValues = (next) => {
        if (valuesProp === undefined)
            setInternalValues(next);
        onValuesChange?.(next);
    };
    const isMeSelected = currentUserId
        ? multiple
            ? values.includes(currentUserId)
            : value === currentUserId
        : true;
    const avatarOf = (o, size) => {
        const p = known.current.get(o.value);
        return (_jsx(Avatar, { name: p?.name ?? o.label, src: p?.avatarUrl ?? null, seed: o.value, decorative: true, ...(size ? { size } : {}) }, o.value));
    };
    const renderValue = (selected) => {
        const first = selected[0];
        if (selected.length === 1 && first) {
            return (_jsxs(_Fragment, { children: [_jsx("span", { className: "mn-assignee-picker__avatars", "aria-hidden": true, children: avatarOf(first, 'xs') }), _jsx("span", { className: "mn-assignee-picker__name", children: first.label })] }));
        }
        return (_jsxs(_Fragment, { children: [_jsx("span", { className: "mn-assignee-picker__avatars", "aria-hidden": true, children: _jsx(AvatarGroup, { label: messages.count(selected.length), size: "xs", max: maxAvatars, children: selected.map((o) => avatarOf(o, undefined)) }) }), _jsx("span", { className: "mn-assignee-picker__name", children: messages.count(selected.length) })] }));
    };
    const meShortcut = currentUserId && showAssignToMe && !isMeSelected
        ? (close) => (_jsx("button", { type: "button", className: "mn-assignee-picker__me", onClick: () => {
                if (multiple)
                    setValues([...values, currentUserId]);
                else {
                    setValue(currentUserId);
                    close();
                }
            }, children: messages.assignToMe }))
        : null;
    return (_jsx(EntityPicker, { ref: ref, ...rest, className: cx('mn-assignee-picker', className), multiple: multiple, value: multiple ? undefined : value, onValueChange: setValue, values: multiple ? values : undefined, onValuesChange: setValues, options: options, onSearch: search, valueOptions: valueOptions, clearOption: allowUnassigned ? { label: messages.unassigned, media: _jsx(UnassignedGlyph, {}) } : undefined, placeholder: placeholder ?? (allowUnassigned ? messages.unassigned : undefined), searchPlaceholder: searchPlaceholder ?? messages.search, renderValue: renderValue, messages: messages, footer: meShortcut || footer
            ? (ctx) => (_jsxs(_Fragment, { children: [meShortcut ? meShortcut(ctx.close) : null, typeof footer === 'function' ? footer(ctx) : footer] }))
            : undefined }));
});
//# sourceMappingURL=assignee-picker.js.map